import { useQuery } from "@tanstack/react-query";
import { AutoComplete, Checkbox, Form, Input, Modal, Spin, Tooltip } from "antd";
import { fetchProductsAPI } from "api/pricerApi";
import { AddTrader, getTraderSuggestions } from "api/teamRunApi";
import {
  CancelButton,
  PrimaryButton,
} from "components/Common/CustomButtons/CustomButtons";
import CompanyTraders from "components/EditTradeDetails/CompanysTrader";
import { BACKEND_KEYS_NAME } from "constants/editTradeDetails";
import { ADD_TRADE_MODAL_INFO } from "constants/teamRun";
import { GlobalContext } from "contexts/GlobalContext";
import { toast } from "helpers/generalHelper";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import "./AddTraderForm.scss";

const SUGGESTION_VALUE_PREFIX = "existing-trader:";

const initialFormData = {
  name: "",
  email: "",
  trading_company_id: "",
};

function normalizeProductId(value) {
  if (value && typeof value === "object") {
    return normalizeProductId(value.id ?? value.product_id ?? value.value);
  }
  if (value == null || value === "") return null;
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : value;
}

function normalizeProductIds(values) {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => normalizeProductId(value))
    .filter((id) => id != null);
}

function hasProductId(ids, id) {
  const target = String(id);
  return ids.some((item) => String(item) === target);
}

function normalizeTraderSuggestion(item) {
  if (!item || typeof item !== "object") return null;
  const id = item.id ?? item.trader_id;
  const name = String(item.name ?? item.label ?? "").trim();
  if (id == null || !name) return null;
  return {
    id,
    name,
    email: item.email ?? "",
    trading_company_id: normalizeProductId(
      item.trading_company_id ?? item.company_id
    ),
    trading_company_name: item.trading_company_name ?? item.company_name ?? "",
    product_ids: normalizeProductIds(item.product_ids ?? item.products),
  };
}

function normalizeTraderSuggestions(payload) {
  if (!Array.isArray(payload)) return [];
  return payload.map(normalizeTraderSuggestion).filter(Boolean);
}

function suggestionOptionValue(trader) {
  return `${SUGGESTION_VALUE_PREFIX}${trader.id}`;
}

function isSuggestionOptionValue(value) {
  return (
    typeof value === "string" && value.startsWith(SUGGESTION_VALUE_PREFIX)
  );
}

function namesMatch(left, right) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function AddTraderForm({
  isOpen,
  setIsOpen = () => {},
  setTradingCompanyId,
  TradingCompanyId,
  setTraderId,
  onTraderChange,
  productId,
}) {
  const [selectedCommodity, setSelectedCommodity] = useState([]);
  const [lockedProductIds, setLockedProductIds] = useState([]);
  const [matchedTrader, setMatchedTrader] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const matchedRef = useRef(null);
  const lockedRef = useRef([]);
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  const { data: commodityData, isLoading: commodityLoading } = useQuery(
    ["trader/products"],
    async () => {
      const res = await fetchProductsAPI();
      return res?.data.data.map((item) => ({
        label: item.title,
        value: item.id,
      }));
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const clearMatch = useCallback(() => {
    if (!matchedRef.current && lockedRef.current.length === 0) return;
    const locked = lockedRef.current;
    matchedRef.current = null;
    lockedRef.current = [];
    setMatchedTrader(null);
    setLockedProductIds([]);
    setSelectedCommodity((prev) =>
      prev.filter((id) => !hasProductId(locked, id))
    );
    setFormData((prev) => ({
      ...prev,
      email: "",
      trading_company_id: "",
    }));
  }, []);

  const applyTrader = useCallback((trader, { replaceName = false } = {}) => {
    const productIds = trader.product_ids;
    const previousLocked = lockedRef.current;
    matchedRef.current = trader;
    lockedRef.current = productIds;
    setMatchedTrader(trader);
    setLockedProductIds(productIds);
    setFormData((prev) => ({
      ...prev,
      name: replaceName ? trader.name : prev.name,
      email: trader.email ?? "",
      trading_company_id: trader.trading_company_id ?? "",
    }));
    setSelectedCommodity((prev) => {
      const extras = prev.filter((id) => !hasProductId(previousLocked, id));
      return Array.from(new Set([...productIds, ...extras]));
    });
  }, []);

  useEffect(() => {
    const name = formData.name.trim();
    const matched = matchedRef.current;
    if (matched && !namesMatch(matched.name, name)) {
      clearMatch();
    }
    if (name.length < 2) {
      setSuggestions([]);
      setIsSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await getTraderSuggestions({ name });
        if (cancelled) return;
        const body = response?.data;
        const payload = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body)
            ? body
            : [];
        const list = normalizeTraderSuggestions(payload);
        setSuggestions(list);
        const exact = list.filter((item) => namesMatch(item.name, name));
        if (exact.length === 1) {
          setSuggestions([]);
          applyTrader(exact[0]);
          return;
        }
        const current = matchedRef.current;
        const stillSelected =
          current &&
          namesMatch(current.name, name) &&
          exact.some((item) => String(item.id) === String(current.id));
        if (current && !stillSelected) {
          clearMatch();
        }
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.name, applyTrader, clearMatch]);

  const onFinish = async () => {
    setIsLoading(true);
    if (TradingCompanyId && TradingCompanyId === formData.trading_company_id) {
      setTradingCompanyId(null);
    }
    try {
      const response = await AddTrader({
        ...formData,
        ...(matchedTrader ? { trader_id: matchedTrader.id } : {}),
        // Includes commodities they already have so the backend can add
        // without dropping the locked ones.
        products: selectedCommodity,
      });
      if (response.data?.data?.message) {
        toast(response?.data?.message, "success");
      }
      setFormData(initialFormData);
      if (
        TradingCompanyId &&
        TradingCompanyId === formData.trading_company_id
      ) {
        setTradingCompanyId(formData.trading_company_id);
        setTimeout(() => {
          setTraderId(response?.data?.data?.id);
          onTraderChange(BACKEND_KEYS_NAME.TRADER, response?.data?.data?.name);
        }, 500);
      }
      setIsOpen(false);
    } catch {
      // Axios interceptor already toasts the backend message.
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTypeChange = (checkedValues) => {
    const locked = lockedRef.current;
    const extras = normalizeProductIds(checkedValues).filter(
      (id) => !hasProductId(locked, id)
    );
    setSelectedCommodity(Array.from(new Set([...locked, ...extras])));
  };

  const hideModal = () => {
    setIsOpen(false);
  };

  const addedProductIds = selectedCommodity.filter(
    (id) => !hasProductId(lockedProductIds, id)
  );
  const isAnyFieldEmpty = matchedTrader
    ? !formData.name || !formData.trading_company_id
    : !formData.name || !formData.email || !formData.trading_company_id;
  const hasCommodityToSave = matchedTrader
    ? addedProductIds.length > 0
    : selectedCommodity.length > 0;
  const submitBlockedReason = isAnyFieldEmpty
    ? "Please fill all the required fields"
    : !hasCommodityToSave
      ? matchedTrader
        ? "Select at least one more commodity to add"
        : "Please fill all the required fields"
      : "";

  const commodityOptions = (commodityData ?? []).map((item) => {
    const value = normalizeProductId(item.value);
    return {
      label: item.label,
      value,
      disabled: hasProductId(lockedProductIds, value),
    };
  });

  const suggestionOptions = suggestions.map((trader) => ({
    value: suggestionOptionValue(trader),
    label: trader.trading_company_name
      ? `${trader.name} — ${trader.trading_company_name}`
      : trader.name,
  }));

  return (
    <div className="">
      <Modal
        open={isOpen}
        width={"35%"}
        centered
        className={dark_Theme && "ant-modal-dark content_dark"}
        onCancel={hideModal}
        title={ADD_TRADE_MODAL_INFO.ADD_TRADER.label}
        bodyStyle={{ minHeight: 250, maxHeight: "70vh", overflow: "auto" }}
        footer={[
          <CancelButton disabled={isLoading} key="back" onClick={hideModal}>
            {ADD_TRADE_MODAL_INFO.CANCEL.label}
          </CancelButton>,
          <Tooltip
            className="ms-2"
            title={submitBlockedReason || undefined}
          >
            <PrimaryButton
              onClick={onFinish}
              loading={isLoading}
              disabled={isAnyFieldEmpty || isLoading || !hasCommodityToSave}
              key="submit"
            >
              {ADD_TRADE_MODAL_INFO.FINISH.label}
            </PrimaryButton>
          </Tooltip>,
        ]}
      >
        <Form
          name="basic"
          className="w-100 h-100 m-auto gap-3 d-flex flex-column justify-content-center align-items-center"
          autoComplete="off"
        >
          <div className="add-trader-form__status">
            <div
              className={
                matchedTrader
                  ? "text-warning"
                  : "add-trader-form__status-measure"
              }
              aria-hidden={matchedTrader ? undefined : true}
            >
              This trader already exists. Greyed commodities are already
              assigned and cannot be changed here. Check more to add them.
            </div>
            {!matchedTrader && (
              <div className="add-trader-form__status-live">
                {isSearching ? "Checking if this trader already exists..." : ""}
              </div>
            )}
          </div>
          <div className="w-100 d-flex gap-3">
            <AutoComplete
              allowClear
              disabled={isLoading}
              value={formData.name}
              options={suggestionOptions}
              filterOption={false}
              open={nameFocused && suggestionOptions.length > 0}
              style={{ flex: 1, minWidth: 0 }}
              popupClassName={dark_Theme ? "dropdown-dark" : ""}
              onFocus={() => setNameFocused(true)}
              onBlur={() => setNameFocused(false)}
              onSelect={(value) => {
                const trader = suggestions.find(
                  (item) => suggestionOptionValue(item) === value
                );
                if (!trader) return;
                setSuggestions([]);
                applyTrader(trader, { replaceName: true });
              }}
              onChange={(value) => {
                if (isSuggestionOptionValue(value)) return;
                setFormData((prev) => ({ ...prev, name: value ?? "" }));
              }}
              placeholder="Name"
            />
            <Input
              required
              disabled={isLoading || Boolean(matchedTrader?.email)}
              style={{ flex: 1, minWidth: 0 }}
              type="email"
              placeholder="Email"
              onChange={(e) => {
                const email = e.target.value;
                setFormData((prev) => ({ ...prev, email }));
              }}
              value={formData.email}
            />
          </div>
          <CompanyTraders
            isPopup
            placeholder={"Trading Company"}
            isDisabled={
              isLoading || Boolean(matchedTrader?.trading_company_id)
            }
            onChange={(tradingCompanyId) => {
              setFormData((prev) => ({
                ...prev,
                trading_company_id: tradingCompanyId,
              }));
            }}
            value={formData.trading_company_id}
            productId={productId}
          />
          <div className="w-100 d-flex justify-content-between align-items-center">
            <label className="fw-medium fs-6">Commodities:</label>
            {commodityLoading ? (
              <Spin size="small" />
            ) : (
              <Checkbox.Group
                className={`add-trader-form__commodities${
                  dark_Theme ? " add-trader-form__commodities--dark" : ""
                }`}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  width: "70%",
                  gap: "10px",
                  color: "white",
                }}
                options={commodityOptions}
                value={selectedCommodity}
                onChange={handleSendTypeChange}
              />
            )}
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default AddTraderForm;
